import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { industryService, Industry } from '../services/industryService';

const industryMethod = [
  {
    title: 'Comprendre les codes du secteur',
    description: 'Nous adaptons le ton, l experience, la structure de vente et les preuves attendues par votre marche.'
  },
  {
    title: 'Relier la tech au business reel',
    description: 'Le site, le CRM, la data et les automatisations doivent soutenir vos operations et votre chiffre d affaires.'
  },
  {
    title: 'Monter en gamme sans complexifier',
    description: 'Nous cherchons un niveau premium visible, tout en gardant une navigation et un pilotage simples.'
  },
  {
    title: 'Executer avec precision',
    description: 'Design, contenu, architecture, conversion et automatisation sont alignes autour d un meme niveau d exigence.'
  }
];

const Jobs = () => {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = industryService.subscribe((updatedIndustries) => {
      setIndustries(updatedIndustries);
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-neon-orange" />
      </div>
    );
  }

  const totalProjects = industries.reduce((sum, industry) => sum + industry.stats.projectsCompleted, 0);

  return (
    <>
      <Helmet>
        <title>Nos Metiers - Couteau Suisse Digital</title>
        <meta
          name="description"
          content="Des solutions digitales premium adaptees a chaque metier. Couteau Suisse Digital structure la croissance par secteur avec design, tech, IA et execution."
        />
      </Helmet>

      <main>
        <section className="py-16 md:py-24 bg-gradient-to-b from-gray-950 via-black to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,106,0,0.22),_transparent_38%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_32%)]" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-200 backdrop-blur"
                >
                  <span className="h-2 w-2 rounded-full bg-neon-orange" />
                  Expertise verticale
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.08 }}
                  className="mt-6 max-w-5xl text-4xl font-bold leading-tight text-white md:text-6xl"
                >
                  Votre metier merite une
                  <span className="bg-gradient-to-r from-neon-orange to-[#FF8A3D] bg-clip-text text-transparent"> execution digitale a son niveau</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.16 }}
                  className="mt-6 max-w-3xl text-lg leading-8 text-gray-300 md:text-xl"
                >
                  Nous adaptons nos systemes, notre design et notre logique d execution aux realites de votre secteur
                  pour vous aider a vendre plus fort, operer plus proprement et gagner en credibilite.
                </motion.p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.24 }}
                className="rounded-[30px] border border-white/10 bg-white/5 p-6 text-white backdrop-blur"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                    <p className="text-sm uppercase tracking-[0.18em] text-neon-orange">Secteurs</p>
                    <div className="mt-3 text-4xl font-bold">{industries.length}</div>
                    <p className="mt-2 text-sm leading-6 text-gray-300">univers metier deja structures dans notre grille d execution</p>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                    <p className="text-sm uppercase tracking-[0.18em] text-neon-orange">Projets</p>
                    <div className="mt-3 text-4xl font-bold">{totalProjects}+</div>
                    <p className="mt-2 text-sm leading-6 text-gray-300">mises en production, transformations et optimisations cumulees</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-18 bg-gradient-to-b from-[#f8f6f2] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-3xl">
              <p className="text-sm uppercase tracking-[0.22em] text-neon-orange">Notre lecture metier</p>
              <h2 className="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">Nous ne dupliquons pas la meme recette partout</h2>
              <p className="mt-5 text-base leading-8 text-gray-600">
                Chaque metier a ses codes, ses objections, ses enjeux de confiance, ses contraintes operationnelles et
                ses leviers de marge. Notre travail est de traduire cela dans un systeme digital juste.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {industryMethod.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="rounded-[26px] border border-gray-200 bg-white p-6 shadow-[0_18px_60px_rgba(17,24,39,0.05)]"
                >
                  <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-white relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
              {industries.map((industry, index) => (
                <motion.div
                  key={industry.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.06 }}
                  viewport={{ once: true }}
                  className="group rounded-[30px] border border-gray-200 bg-white p-7 shadow-[0_22px_70px_rgba(17,24,39,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_26px_80px_rgba(17,24,39,0.10)]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-neon-orange/10 text-3xl">
                        {industry.icon}
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">{industry.name}</h2>
                        <p className="mt-1 text-sm text-gray-500">{industry.stats.projectsCompleted}+ projets realises</p>
                      </div>
                    </div>
                    <div className="rounded-full border border-gray-200 bg-[#f8f6f2] px-3 py-1 text-xs uppercase tracking-[0.16em] text-gray-500">
                      {industry.stats.roi} ROI
                    </div>
                  </div>

                  <p className="mt-6 text-base leading-7 text-gray-700">{industry.description}</p>

                  <div className="mt-6 rounded-2xl bg-[#fbfaf8] p-5">
                    <p className="text-xs uppercase tracking-[0.16em] text-gray-500">Enjeux clefs</p>
                    <div className="mt-4 space-y-3">
                      {industry.challenges.slice(0, 3).map((challenge) => (
                        <div key={challenge} className="flex items-start gap-3">
                          <div className="mt-1 h-2.5 w-2.5 rounded-full bg-neon-orange" />
                          <p className="text-sm leading-6 text-gray-600">{challenge}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-between text-sm text-gray-500">
                    <span>
                      <span className="font-semibold text-neon-orange">{industry.stats.clients}+</span> clients
                    </span>
                    <span>
                      <span className="font-semibold text-neon-orange">{industry.stats.successRate}</span> satisfaction
                    </span>
                  </div>

                  <Link
                    to={`/activites/${industry.id}`}
                    className="mt-7 inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-neon-orange to-[#FF4500] px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-neon-orange/10"
                  >
                    Decouvrir les solutions du secteur
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-gradient-to-b from-gray-950 via-black to-gray-900 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,106,0,0.16),_transparent_40%)]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-white md:text-5xl"
            >
              Votre secteur a des codes precis.
              <span className="bg-gradient-to-r from-neon-orange to-[#FF8A3D] bg-clip-text text-transparent"> Votre systeme digital doit les respecter.</span>
            </motion.h2>

            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-gray-300">
              Si vous voulez une execution plus credible, plus premium et mieux raccordee a votre marche, nous pouvons
              cadrer cela ensemble.
            </p>

            <Link
              to="/contact"
              className="mt-10 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-neon-orange to-[#FF4500] px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-neon-orange/20"
            >
              Discuter avec un expert
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default Jobs;
